import React, { useState, useEffect } from 'react';
import { Users, BarChart3, Presentation, Briefcase, Activity, CheckCircle, PieChart, ShieldAlert, TrendingUp, Percent } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface DealRecord {
  id: string;
  clientName: string;
  companyName?: string;
  dealValue: string;
  status: string;
  date?: string;
}

const pipelineData = [
  { month: 'JAN', deals: 4 },
  { month: 'FEB', deals: 7 },
  { month: 'MAR', deals: 5 },
  { month: 'APR', deals: 12 },
  { month: 'MAY', deals: 15 },
  { month: 'JUN', deals: 22 },
];

export const CrmDashboardTerminal: React.FC = () => {
  const [activeTab, setActiveTab ] = useState<'CRM' | 'DASHBOARD' | 'SLIDES'>(() => {
    return (localStorage.getItem('nexorax_crm_active_tab') as 'CRM' | 'DASHBOARD' | 'SLIDES') || 'CRM';
  });

  useEffect(() => {
    localStorage.setItem('nexorax_crm_active_tab', activeTab);
  }, [activeTab]);

  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'deals'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dealsList: DealRecord[] = [];
      snapshot.forEach((doc) => {
        dealsList.push({ id: doc.id, ...doc.data() } as DealRecord);
      });
      setDeals(dealsList);
      setLoadingDeals(false);
    }, (err) => {
      console.error("[CRM LOG] Firestore loaded with error. Fallback metrics standard active.", err);
      setLoadingDeals(false);
    });
    return () => unsubscribe();
  }, []);

  const baselineLeads = [
    { id: '1', clientName: 'Apex Global Syndicate', status: 'MNDA SIGNED', dealValue: '1500000', companyName: 'Apex Global Syndicate' },
    { id: '2', clientName: 'Nexus Tier-1 Group', status: 'DISCOVERY', dealValue: '850000', companyName: 'Nexus Tier-1 Group' },
    { id: '3', clientName: 'Vanguard Holdings', status: 'PITCH DECK SENT', dealValue: '3200000', companyName: 'Vanguard Holdings' },
    { id: '4', clientName: 'Sona', status: 'Active - Deployed', dealValue: '15000', companyName: "Sona's Kitchen Dunfermline" },
    { id: '5', clientName: 'Staff Coordinator', status: 'Pending Activation', dealValue: '10000', companyName: 'NexoraX Operations' },
    { id: '6', clientName: 'Claire', status: 'Pending Activation', dealValue: '35000', companyName: 'Secure Invoice Register Acquisition' }
  ];

  const relevantLeads = deals.length > 0 ? deals : baselineLeads;

  const totalLeadsCount = relevantLeads.length;
  const mndaSignedCount = relevantLeads.filter(
    (lead) => lead.status?.trim().toUpperCase() === 'MNDA SIGNED'
  ).length;

  const conversionRateValue = totalLeadsCount > 0 
    ? ((mndaSignedCount / totalLeadsCount) * 100).toFixed(1) 
    : '0.0';

  const mndaTrendData = React.useMemo(() => {
    const dataList = [];
    const today = new Date();
    
    // Create base map of days
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dataList.push({
        dateStr,
        label,
        count: 0,
        cumulative: 0,
      });
    }

    const mndaLeads = relevantLeads.filter(
      (lead) => lead.status?.trim().toUpperCase() === 'MNDA SIGNED'
    );

    mndaLeads.forEach((lead, idx) => {
      let leadDateStr = lead.date;
      if (!leadDateStr) {
        const d = new Date(today);
        // Distribute nicely across the 30 days window so the graph starts lower and rises elegantly
        d.setDate(today.getDate() - ((idx * 7 + 4) % 28));
        leadDateStr = d.toISOString().split('T')[0];
      }
      
      const dayObj = dataList.find(item => item.dateStr === leadDateStr);
      if (dayObj) {
        dayObj.count += 1;
      } else {
        if (dataList.length > 0) {
          dataList[0].count += 1;
        }
      }
    });

    let runningSum = 0;
    dataList.forEach(item => {
      runningSum += item.count;
      item.cumulative = runningSum;
    });

    return dataList;
  }, [relevantLeads]);

  return (
    <div className="flex flex-col h-full space-y-6 max-w-6xl mx-auto w-full pb-10 mt-8 relative">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-indigo-500/20 pb-6 mb-2 gap-4 shrink-0">
        <div>
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block mb-2">INTEGRATION NODE 26: DEAL FLOW SECURED</span>
          <h2 className="text-2xl font-bold text-white uppercase tracking-widest font-sans drop-shadow-md flex items-center gap-3">
            <Briefcase className="w-6 h-6 text-indigo-400 shrink-0" />
            CRM, DASHBOARD & SLIDE DECK
          </h2>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <button className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600/80 to-purple-800/80 hover:from-indigo-500 hover:to-purple-700 border border-indigo-400/50 transition-all px-6 py-2.5 rounded shadow-[0_0_15px_rgba(99,102,241,0.3)] text-white group">
            <CheckCircle className="w-4 h-4 shadow-[0_0_10px_rgba(255,255,255,0.5)] rounded-full text-white" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold">GENERATE REPORT</span>
          </button>
        </div>
      </div>
      
      {/* Navigation Tabs */}
      <div className="flex space-x-2 border-b border-slate-800 pb-px mb-4">
        <button 
          onClick={() => setActiveTab('CRM')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all ${
            activeTab === 'CRM' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Users className="w-4 h-4" /> CRM Module
        </button>
        <button 
          onClick={() => setActiveTab('DASHBOARD')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all ${
            activeTab === 'DASHBOARD' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <BarChart3 className="w-4 h-4" /> Dashboard Tab
        </button>
        <button 
          onClick={() => setActiveTab('SLIDES')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all ${
            activeTab === 'SLIDES' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Presentation className="w-4 h-4" /> Slide Deck Node
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg overflow-hidden flex flex-col min-h-[500px]">
        {activeTab === 'CRM' && (
          <div className="p-8 flex flex-col h-full space-y-6 overflow-y-auto custom-scrollbar">
            <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans mb-2">NexoraX CRM Matrix</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-black/40 border border-indigo-500/30 rounded p-4 flex flex-col">
                <span className="text-indigo-400 font-mono text-[10px] uppercase font-bold tracking-widest">Active Whales</span>
                <span className="text-3xl text-white font-bold mt-2">142</span>
              </div>
              <div className="bg-black/40 border border-emerald-500/30 rounded p-4 flex flex-col">
                <span className="text-emerald-400 font-mono text-[10px] uppercase font-bold tracking-widest">Deal Flow Velocity</span>
                <span className="text-3xl text-white font-bold mt-2">£4.2M</span>
              </div>
              <div className="bg-black/40 border border-rose-500/30 rounded p-4 flex flex-col">
                <span className="text-rose-400 font-mono text-[10px] uppercase font-bold tracking-widest">Pending Signatures</span>
                <span className="text-3xl text-white font-bold mt-2">18</span>
              </div>
              <div className="bg-black/40 border border-[#22d3ee]/45 shadow-[0_0_15px_rgba(34,211,238,0.1)] rounded p-4 flex flex-col relative group overflow-hidden">
                <span className="text-[#22d3ee] font-mono text-[10px] uppercase font-bold tracking-widest flex items-center gap-1">
                   <Percent className="w-3.5 h-3.5 text-[#22d3ee] shrink-0" /> Conversion Rate
                </span>
                <div className="flex justify-between items-end mt-2">
                   <span className="text-3xl text-white font-black">{conversionRateValue}%</span>
                   <span className="text-[10px] font-mono text-slate-400 pb-1">{mndaSignedCount}/{totalLeadsCount} MNDA</span>
                </div>
                {/* Micro progress indicator */}
                <div className="w-full bg-slate-950 h-1 rounded-full overflow-hidden mt-3 border border-slate-900">
                  <div 
                    className="bg-gradient-to-r from-cyan-400 to-indigo-500 h-full rounded-full transition-all duration-1000" 
                    style={{ width: `${Math.min(100, parseFloat(conversionRateValue))}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="flex-1 border border-slate-700/50 rounded overflow-hidden flex flex-col">
              <div className="bg-black/60 px-4 py-3 border-b border-slate-700/50 flex justify-between items-center">
                 <span className="text-xs font-mono text-slate-300 font-bold uppercase tracking-widest">High-Ticket Client Pipeline</span>
                 <button className="text-[10px] font-mono text-indigo-400 uppercase tracking-widest border border-indigo-500/50 px-2 py-1 rounded hover:bg-indigo-500/20 transition-colors">Add Entity</button>
              </div>
              <div className="flex-1 p-0 overflow-x-auto">
                <table className="w-full text-left font-mono text-xs">
                  <thead className="bg-[#0b0f19] text-slate-500 uppercase tracking-widest text-[9px] border-b border-slate-800">
                    <tr>
                      <th className="px-4 py-3 font-medium">Entity Name</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Capital</th>
                      <th className="px-4 py-3 font-medium">Last Contact</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300 divide-y divide-slate-800/50">
                    {relevantLeads.map((lead, idx) => {
                      let formattedValue = lead.dealValue;
                      if (formattedValue && !formattedValue.startsWith('£')) {
                        const numValue = parseFloat(formattedValue.replace(/[^0-9.]/g, ''));
                        if (!isNaN(numValue)) {
                          if (numValue >= 1000000) {
                            formattedValue = `£${(numValue / 1000000).toFixed(1)}M`;
                          } else if (numValue >= 1000) {
                            formattedValue = `£${(numValue / 1000).toFixed(0)}K`;
                          } else {
                            formattedValue = `£${numValue.toLocaleString()}`;
                          }
                        } else {
                          formattedValue = `£${lead.dealValue}`;
                        }
                      } else if (!formattedValue) {
                        formattedValue = '£0';
                      }
                      
                      const isMnda = lead.status?.trim().toUpperCase() === 'MNDA SIGNED';
                      const isDiscovery = lead.status?.trim().toUpperCase() === 'DISCOVERY' || lead.status?.trim().toLowerCase().includes('pending');
                      const isPitch = lead.status?.trim().toUpperCase() === 'PITCH DECK SENT' || lead.status?.trim().toLowerCase().includes('active');
                      
                      let statusBadge = (
                        <span className="text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded border border-amber-500/30 text-[10px] uppercase font-bold tracking-wider">
                          {lead.status}
                        </span>
                      );
                      
                      if (isMnda) {
                        statusBadge = (
                          <span className="text-emerald-400 bg-emerald-950/20 px-2 py-0.5 rounded border border-emerald-500/30 text-[10px] uppercase font-bold tracking-wider">
                            MNDA SIGNED
                          </span>
                        );
                      } else if (isDiscovery) {
                        statusBadge = (
                          <span className="text-indigo-400 bg-indigo-950/20 px-2 py-0.5 rounded border border-indigo-500/30 text-[10px] uppercase font-bold tracking-wider">
                            {lead.status}
                          </span>
                        );
                      } else if (isPitch) {
                        statusBadge = (
                          <span className="text-cyan-400 bg-cyan-950/20 px-2 py-0.5 rounded border border-cyan-500/30 text-[10px] uppercase font-bold tracking-wider">
                            {lead.status}
                          </span>
                        );
                      }

                      const contactText = lead.date || `${idx + 1} DAY AGO`;

                      return (
                        <tr key={lead.id || idx} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-4 py-3 font-sans font-semibold text-white">
                            {lead.clientName}
                            {lead.companyName && lead.companyName !== lead.clientName && (
                              <span className="block text-[10px] text-slate-500 font-mono font-normal">
                                {lead.companyName}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">{statusBadge}</td>
                          <td className="px-4 py-3 font-mono font-bold text-slate-200">{formattedValue}</td>
                          <td className="px-4 py-3 text-slate-500 uppercase">{contactText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'DASHBOARD' && (
          <div className="p-8 flex flex-col h-full space-y-6 overflow-y-auto custom-scrollbar">
             <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans mb-2">Executive Reporting Dashboard</h3>
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
               <div className="bg-black/40 border border-slate-700/50 rounded flex flex-col p-6">
                 <span className="text-xs font-mono text-indigo-400 uppercase tracking-widest font-bold mb-4">Deal Flow Generation (6 MO)</span>
                 <div className="flex-1 min-h-[200px]">
                   <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                     <AreaChart data={pipelineData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                       <defs>
                         <linearGradient id="colorDeals" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                           <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                         </linearGradient>
                       </defs>
                       <YAxis hide domain={['dataMin', 'dataMax + 5']} />
                       <Tooltip 
                         contentStyle={{ backgroundColor: '#020617', border: '1px solid #1e293b', fontSize: '10px', textTransform: 'uppercase' }} 
                         itemStyle={{ color: '#6366f1' }} 
                         labelStyle={{ display: 'none' }}
                       />
                       <Area type="monotone" dataKey="deals" stroke="#6366f1" fillOpacity={1} fill="url(#colorDeals)" strokeWidth={2} />
                     </AreaChart>
                   </ResponsiveContainer>
                 </div>
               </div>

               {/* New Conversion Rate Widget */}
               <div className="bg-black/40 border border-[#22d3ee]/30 hover:shadow-[0_0_15px_rgba(34,211,238,0.1)] transition-all rounded flex flex-col p-6 space-y-4">
                 <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest font-bold flex items-center gap-1.5 mb-2">
                   <Percent className="w-4 h-4 text-cyan-400" /> Conversion Matrix
                 </span>
                 <div className="flex-1 flex flex-col justify-center items-center py-6 text-center border border-slate-800/50 bg-[#050914]/40 rounded p-4">
                    <span className="text-4xl lg:text-5xl font-black text-white tracking-widest block font-sans [text-shadow:0_0_15px_rgba(34,211,238,0.4)]">
                      {conversionRateValue}%
                    </span>
                    <span className="text-[10px] font-mono text-[#22d3ee] uppercase tracking-[0.15em] font-bold mt-2">
                      MNDA Signed Rate
                    </span>
                    <div className="w-full bg-slate-950 h-2.5 rounded-full overflow-hidden mt-6 border border-slate-900">
                      <div 
                        className="bg-gradient-to-r from-cyan-400 to-indigo-500 h-full rounded-full transition-all duration-1000" 
                        style={{ width: `${Math.min(100, parseFloat(conversionRateValue))}%` }}
                      ></div>
                    </div>
                 </div>
                 <div className="text-[10px] grid grid-cols-2 gap-y-2 font-mono border-t border-slate-800 pt-4 text-slate-400">
                   <div>
                     <span className="text-slate-600 uppercase text-[9px] block">TOTAL LEADS</span>
                     <span className="block text-white font-bold">{totalLeadsCount}</span>
                   </div>
                   <div className="text-right">
                     <span className="text-slate-600 uppercase text-[9px] block">MNDA SIGNED</span>
                     <span className="block text-emerald-400 font-bold">{mndaSignedCount}</span>
                   </div>
                   <div className="col-span-full text-center border-t border-slate-800/30 pt-2 text-slate-500 uppercase tracking-widest text-[8px] font-bold">
                     [NexoraX Core Agreement Ledger Index]
                   </div>
                 </div>
               </div>
               
               <div className="bg-black/40 border border-slate-700/50 rounded flex flex-col p-6 space-y-4">
                 <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest font-bold mb-2">System Telemetry Log</span>
                 <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                   <div className="flex items-center gap-2">
                     <ShieldAlert className="w-4 h-4 text-emerald-400" />
                     <span className="font-mono text-[10px] text-slate-300 uppercase tracking-widest">Oracle Node Sync</span>
                   </div>
                   <span className="font-mono text-[10px] text-emerald-500">STABLE</span>
                 </div>
                 <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                   <div className="flex items-center gap-2">
                     <Activity className="w-4 h-4 text-cyan-400" />
                     <span className="font-mono text-[10px] text-slate-300 uppercase tracking-widest">Capital Routing Gateway</span>
                   </div>
                   <span className="font-mono text-[10px] text-cyan-500">ACTIVE</span>
                 </div>
                 <div className="flex items-center justify-between pb-2">
                   <div className="flex items-center gap-2">
                     <PieChart className="w-4 h-4 text-indigo-400" />
                     <span className="font-mono text-[10px] text-slate-300 uppercase tracking-widest">Franchise Allocation</span>
                   </div>
                   <span className="font-mono text-[10px] text-indigo-500">OPTIMIZED</span>
                 </div>
               </div>
             </div>

             {/* MNDA signed velocity trend LineChart over 30 days */}
             <div className="border border-slate-700/50 bg-black/40 rounded-xl p-6 flex flex-col space-y-4 mt-6">
               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-2 border-b border-slate-800/60">
                 <div>
                   <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest font-semibold flex items-center gap-1.5">
                     <TrendingUp className="w-4 h-4 text-cyan-400" /> MNDA Signature Velocity Trend (30 Days)
                   </span>
                   <p className="text-[11px] text-slate-500 font-sans mt-0.5">Real-time metrics tracking the signature milestones and daily velocity of NexoraX agreements.</p>
                 </div>
                 <div className="flex items-center gap-2 bg-slate-900/60 px-3 py-1 rounded border border-slate-850 shrink-0">
                   <span className="inline-block w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                   <span className="text-[9px] font-mono text-cyan-400 uppercase tracking-widest font-bold">System Telemetry Live</span>
                 </div>
               </div>
               
               <div className="h-[250px] w-full pt-4">
                 <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                   <LineChart data={mndaTrendData} margin={{ top: 10, right: 15, left: -20, bottom: 0 }}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} />
                     <XAxis 
                       dataKey="label" 
                       stroke="#64748b" 
                       fontSize={9} 
                       tickLine={false} 
                       axisLine={false}
                       dy={8}
                     />
                     <YAxis 
                       stroke="#64748b" 
                       fontSize={9} 
                       tickLine={false} 
                       axisLine={false}
                       allowDecimals={false}
                       dx={-4}
                     />
                     <Tooltip
                       contentStyle={{ 
                         backgroundColor: '#020617', 
                         border: '1px solid #22d3ee', 
                         borderRadius: '8px', 
                         boxShadow: '0 0 15px rgba(34,211,238,0.15)',
                         fontSize: '11px', 
                         fontFamily: 'monospace' 
                       }} 
                       itemStyle={{ color: '#22d3ee' }}
                       labelClassName="text-slate-400 uppercase tracking-wide text-[9px] font-mono font-bold"
                     />
                     <Line 
                       type="monotone" 
                       dataKey="cumulative" 
                       stroke="#22d3ee" 
                       strokeWidth={3} 
                       dot={{ r: 3, fill: '#050914', stroke: '#22d3ee', strokeWidth: 2 }} 
                       activeDot={{ r: 6, fill: '#22d3ee', stroke: '#050914', strokeWidth: 2 }} 
                       name="Cumulative Signed" 
                     />
                     <Line 
                       type="monotone" 
                       dataKey="count" 
                       stroke="#6366f1" 
                       strokeWidth={1.5} 
                       strokeDasharray="4 4" 
                       dot={{ r: 2, fill: '#050914', stroke: '#6366f1', strokeWidth: 1 }} 
                       activeDot={{ r: 4, fill: '#6366f1', stroke: '#050914', strokeWidth: 1 }} 
                       name="New Signatures" 
                     />
                   </LineChart>
                 </ResponsiveContainer>
               </div>
             </div>
          </div>
        )}

        {activeTab === 'SLIDES' && (
          <div className="p-8 flex flex-col h-full space-y-6 overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-end mb-2">
              <div>
                <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans mb-1">Imperial Pitch Deck Generator</h3>
                <p className="text-sm font-sans text-slate-400 max-w-2xl">Zero-friction slide automation for boardroom deployment. Instantly compile dark-mode corporate assets.</p>
              </div>
              <button className="bg-slate-800 hover:bg-slate-700 border border-slate-600 px-4 py-2 rounded text-white transition-colors font-mono text-[10px] uppercase font-bold tracking-widest">
                + NEW DECK
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Resource resource PITCH', date: 'UPDATED 2H AGO', slides: 18 },
                { name: 'FRANCHISE EXPANSION DECK', date: 'UPDATED 1D AGO', slides: 42 },
                { name: 'NEXORAX V4 ARCHITECTURE', date: 'UPDATED 3D AGO', slides: 24 },
                { name: 'BOARDROOM TAKEOVER', date: 'UPDATED 1W AGO', slides: 12 },
              ].map((deck, idx) => (
                <div key={idx} className="bg-black/50 border border-slate-700 rounded overflow-hidden group cursor-pointer hover:border-indigo-500 transition-colors flex flex-col">
                  <div className="aspect-video bg-slate-900 border-b border-slate-800 flex items-center justify-center p-4 relative overflow-hidden">
                     <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:10px_10px]"></div>
                     <span className="font-sans font-black text-white text-lg tracking-tighter uppercase text-center relative z-10">{deck.name}</span>
                  </div>
                  <div className="p-4 flex justify-between items-center bg-[#050914] group-hover:bg-[#0a1020] transition-colors">
                    <span className="font-mono text-[9px] text-slate-500 tracking-widest uppercase">{deck.date}</span>
                    <span className="font-mono text-[10px] text-indigo-400 font-bold tracking-widest">{deck.slides} SLIDES</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
